import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import React from 'react'
import { toast } from 'react-toastify'

import { createUser, dbContextsQuery, deleteUser, resetPassword, usersQuery } from '@/api/users'
import { Spinner } from '@/components/Spinner'
import UsersIndex from '@/features/users/UsersIndex'

export type UsersSearchParams = {
  dbContext?: string
  q?: string
}

export const Route = createFileRoute('/users/')({
  loaderDeps: ({ search }) => ({
    ...search,
    dbContext: search.dbContext,
  }),
  loader: async ({ context: { queryClient }, deps }) => {
    const dbContexts = await queryClient.fetchQuery(dbContextsQuery)
    const currContext = (deps.dbContext ?? Object.keys(dbContexts)[0]) as string

    await queryClient.ensureQueryData(usersQuery(currContext))

    return {
      dbContexts,
      currContext,
    }
  },
  validateSearch: (search: Record<string, unknown>): UsersSearchParams => {
    // validate and parse the search params into a typed state
    return {
      q: search.q as string,
    }
  },
  component: UsersComponent,
  pendingComponent() {
    return <Spinner />
  },
})

function UsersComponent() {
  const queryClient = useQueryClient()
  const { currContext, dbContexts } = Route.useLoaderData()

  const { data: users } = useSuspenseQuery(usersQuery(currContext))

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteUser(currContext, id),
    onSuccess: (res, id) => {
      queryClient.setQueryData(usersQuery(currContext).queryKey, (oldData) => {
        if (oldData) {
          return oldData.filter((item) => item.idUser !== id)
        }
      })

      toast.success('Account was deleted successfully')
    },
    onError: (err) => {
      toast.error('Error deleting account')
    },
  })

  const createMutation = useMutation({
    mutationFn: ({ newUser, changePassword }: any) => createUser(newUser, changePassword),
    onSuccess: (res) => {
      queryClient.resetQueries({ queryKey: usersQuery(currContext).queryKey })

      queryClient.setQueryData(usersQuery(currContext).queryKey, (oldData) => {
        if (oldData) {
          return [...oldData, res]
        }
      })

      toast.success('Account was created successfully')
    },
    onError: (err) => {
      toast.error('Error creating account')
    },
  })

  const resetPasswordMutation = useMutation({
    mutationFn: ({ idUser, password }: any) => resetPassword(currContext, idUser, password),
    onSuccess: (res) => {
      toast.success('Password was reset successfully')
    },
    onError: (err) => {
      toast.error('Error resetting password')
    },
  })

  return (
    <UsersIndex
      users={users}
      dbContexts={dbContexts}
      currContext={currContext}
      deleteUser={deleteMutation.mutate}
      createUser={(newUser, changePassword) => createMutation.mutate({ newUser, changePassword })}
      resetPassword={(idUser, password) => resetPasswordMutation.mutate({ idUser, password })}
    />
  )
}
